"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Boxes, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const offeringCards = [{
  title: "Related Products",
  description: "Explore industry-standard analytical instruments, chromatography systems, and laboratory hardware tailored for regulated workflows.",
  icon: Boxes,
  href: "/ProductsPage",
  ctaLabel: "View all products",
  tag: "Instrumentation & Systems",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/related_products_lab.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/related_products_lab.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/related_products_lab.png-wvc-sizes",
    alt: "Laboratory analytical technology and instrumentation"
  },
  highlights: ["Chromatography & spectroscopy systems", "Benchtop analyzers & detector units", "Consumables, columns, and certified spares"]
}, {
  title: "Related Services",
  description: "Complete lifecycle support, including installation qualification, annual calibration, preventative maintenance, and compliance audit support.",
  icon: Wrench,
  href: "/ServicesPage",
  ctaLabel: "Explore service plans",
  tag: "Compliance & Maintenance",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/related_services_engineering.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/related_services_engineering.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/related_services_engineering.png-wvc-sizes",
    alt: "Engineer qualifying analytical equipment in cleanroom environment"
  },
  highlights: ["IQ/OQ/PQ validation protocols", "Scheduled preventive maintenance", "Rapid on-site response across Ireland and the UK"]
}];
export default function Offerings() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedofferings" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-primary text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Complete Capabilities
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground tracking-tight leading-tight">
            Explore our solutions and ongoing support
          </h2>
          <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed">
            From precision instrumentation procurement to on-site validation and lifecycle servicing, discover how Antech Solutions supports regulated laboratories.
          </p>
        </div>

        {/* 2-Column Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {offeringCards.map((offering, i) => {
          const Icon = offering.icon;
          return <motion.div key={offering.title} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.5,
            delay: i * 0.15
          }} className="h-full flex">
                <Card className="flex-1 flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl p-0">
                  <div>
                    {/* Visual Media Header */}
                    <div className="relative w-full h-52 sm:h-64 overflow-hidden bg-muted">
                      <img src={offering.image.src} data-wvc-srcset={offering.image.srcset} data-wvc-sizes={offering.image.sizes} alt={offering.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-xs text-foreground px-3 py-1 rounded-full text-xs font-medium border border-border">
                        {offering.tag}
                      </div>
                      <div className="absolute bottom-4 right-4 bg-primary text-primary-foreground p-2.5 rounded-lg shadow-sm">
                        <Icon className="w-5 h-5" aria-hidden="true" />
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-3">
                      <CardTitle className="text-2xl font-bold font-default text-foreground">
                        {offering.title}
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground mt-2 leading-relaxed">
                        {offering.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2 pb-6">
                      <div className="border-t border-border pt-4">
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider block mb-3">
                          Key Areas
                        </span>
                        <ul className="space-y-2.5">
                          {offering.highlights.map((highlight, itemIdx) => <li key={itemIdx} data-index={itemIdx} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                              <span>{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0 pb-6 border-t border-border mt-auto">
                    <Button asChild variant="default" size="lg" className="w-full justify-between group mt-6">
                      <Link to={offering.href}>
                        <span>{offering.ctaLabel}</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}